import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { appConfirmMessage } from '../../../utils/appConfirm';
import {
  FaArrowLeft,
  FaBell,
  FaEnvelope,
  FaPhoneAlt,
  FaPlus,
  FaStar,
} from 'react-icons/fa';
import { FiEdit2, FiMail, FiMessageCircle, FiPhone } from 'react-icons/fi';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import { mapStatusToDisplay } from '../../ProjectSection/utils/projectUtils';
import { ProfileBookingProvider } from '../../ProfilePages/ProfileBookingContext';
import {
  bookingIdsForCustomer,
  customerInitials,
  dedupeById,
  formatDate,
  formatDateRange,
  isPaidInvoice,
  isPendingLead,
  isRecentCustomerProject,
  moneySum,
  projectTone,
  proposalPrefillFromClient,
} from './customerUtils';

const QuotationForm = lazy(() =>
  import('../../ProfilePages/components/QuotationForm'),
);
const QuotationSendEmailModal = lazy(() =>
  import('../../ProfilePages/components/QuotationSendEmail'),
);
const AddProject = lazy(() =>
  import('../../ProjectSection/components/AddProject'),
);
const CustomerProjectDetail = lazy(() => import('./CustomerProjectDetail'));
const CustomerCreateInvoice = lazy(() => import('./CustomerCreateInvoice'));
const ProjectDocumentModal = lazy(() => import('./ProjectDocumentModal'));
const CustomerEmailModal = lazy(() => import('./CustomerEmailModal'));
const CustomerCallModal = lazy(() => import('./CustomerCallModal'));
const CustomerMessageModal = lazy(() => import('./CustomerMessageModal'));
const CreateProposalModal = lazy(() =>
  import('../../Proposals/components/CreateProposalModal'),
);
const CustomerCreateContract = lazy(() => import('./CustomerCreateContract'));

const FIND_CUSTOMER_PROJECTS = `
  query FindCustomerProjects($bookingId: String!) {
    findBookingProject(bookingId: $bookingId) {
      id
      projectId
      name
      status
      startDate
      deadLine
      attention
      deliveryAddress
      purchaseOrderNumber
      createdAt
      seriesRootId
      billingCadence
      recurrenceFrequency
      projectAddress
      booking { id address preferredDate message }
    }
  }
`;

const FIND_CUSTOMER_CONTRACTS = `
  query FindCustomerContracts {
    findAllClientContracts {
      id
      contractNumber
      quotationNumber
      proposalNumber
      bookingId
      clientEmail
      title
      status
      value
      versionNumber
      contractDate
      sentAt
      signedAt
    }
  }
`;

const FIND_CUSTOMER_PROPOSALS = `
  query FindCustomerProposals {
    findAllProposals {
      id
      proposalNumber
      title
      clientEmail
      projectId
      status
      value
      version
      sentAt
      createdAt
    }
  }
`;

const FIND_CUSTOMER_QUOTES = `
  query FindCustomerQuotations($bookingId: String!) {
    findBookingQuotation(bookingId: $bookingId) {
      id
      quotationNumber
      versionNumber
      quotationStage
      clientStatus
      listTotal
      listProjectCode
      createdAt
    }
  }
`;

const FIND_CUSTOMER_INVOICES = `
  query FindCustomerInvoices($bookingId: String!) {
    findBookingInvoice(bookingId: $bookingId) {
      id
      invoiceNumber
      paymentStatus
      invoiceStage
      listTotal
      listProjectCode
      listProjectName
      listDueDate
      createdAt
    }
  }
`;

/** Same mutation the Project section uses to change a project's status. */
const UPDATE_BOOKING_STATUS = `
  mutation UpdateBookingStatus($id: String!, $status: taskStatus!) {
    updateBooking(updateBookingInput: { id: $id, status: $status }) {
      id
      status
    }
  }
`;

/** Requests a client raised from the client portal await admin approval. */
const FIND_CLIENT_REQUESTS = `
  query FindCustomerBookingRequests($page: Float!, $limit: Float!) {
    findAllBooking(page: $page, limit: $limit, includeSignUrls: false) {
      data {
        id
        fullName
        email
        phone
        address
        status
        createdAt
        preferredDate
        message
        bookingFrequency
        recordType
        clientRequestKind
      }
    }
  }
`;


const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function lastSixMonthBuckets() {
  const now = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    return {
      key: `${date.getFullYear()}-${date.getMonth()}`,
      label: MONTH_SHORT[date.getMonth()],
      income: 0,
      outstanding: 0,
    };
  });
}

function invoiceMonthKey(invoice) {
  const date = invoice?.createdAt ? new Date(invoice.createdAt) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${date.getMonth()}`;
}

/**
 * Bookings have no human-readable code column, so derive a stable short ref
 * from the row id for display (REQ-A1B2C3).
 */
function requestRef(id) {
  const raw = String(id || '').replace(/[^a-z0-9]/gi, '');
  return raw ? `REQ-${raw.slice(0, 6).toUpperCase()}` : 'REQ-—';
}

/** Service name lives packed in bookingFrequency as "Source|Label". */
function serviceRequested(request) {
  const raw = String(request?.bookingFrequency || '').trim();
  if (!raw) return 'Service request';
  const parts = raw.split('|').map((part) => part.trim()).filter(Boolean);
  return parts[parts.length - 1] || 'Service request';
}

/** Portal requests record whether the client wants a quote or a booked service. */
function isServiceBooking(request) {
  return String(request?.clientRequestKind || '').toLowerCase() === 'booking';
}

function EmptyHint({ children }) {
  return <div className="customer-muted customer-empty-hint">{children}</div>;
}

export default function CustomerProfile({ customer, onBack, onEdit }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [viewingProject, setViewingProject] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [docModal, setDocModal] = useState(null);
  const [clientRequests, setClientRequests] = useState([]);
  const [decidingRequestId, setDecidingRequestId] = useState(null);
  const [acceptingRequest, setAcceptingRequest] = useState(null);
  const [enquiryRequest, setEnquiryRequest] = useState(null);
  /** Quotation saved via "Issue to client", waiting on the send-email step. */
  const [issuingQuote, setIssuingQuote] = useState(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [creatingProposal, setCreatingProposal] = useState(false);
  const [creatingContract, setCreatingContract] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [writingEmail, setWritingEmail] = useState(false);
  const [calling, setCalling] = useState(false);
  const [messaging, setMessaging] = useState(false);

  const loadRecords = useCallback(
    async (silent = false) => {
      if (!customer?.id) return;
      const ids = bookingIdsForCustomer(customer);
      if (!silent) {
        setLoading(true);
        setError('');
      }
      try {
        const chunks = await Promise.all(
          ids.map(async (bookingId) => {
              const [projectRes, quoteRes, invoiceRes] = await Promise.all([
                graphqlRequest(FIND_CUSTOMER_PROJECTS, { bookingId }).catch(
                  () => null,
                ),
                graphqlRequest(FIND_CUSTOMER_QUOTES, { bookingId }).catch(
                  () => null,
                ),
                graphqlRequest(FIND_CUSTOMER_INVOICES, { bookingId }).catch(
                  () => null,
                ),
              ]);
            return {
              projects: (projectRes?.findBookingProject || [])
                .filter((row) => !row.seriesRootId)
                .map((row) => ({
                  ...row,
                  booking: row.booking?.id ? row.booking : { id: bookingId },
                })),
              quotations: quoteRes?.findBookingQuotation || [],
              invoices: invoiceRes?.findBookingInvoice || [],
            };
          }),
        );
        setProjects(dedupeById(chunks.flatMap((chunk) => chunk.projects)));
        const loadedQuotes = dedupeById(
          chunks.flatMap((chunk) => chunk.quotations),
        );
        setQuotations(loadedQuotes);
        setInvoices(dedupeById(chunks.flatMap((chunk) => chunk.invoices)));

        // Needed so a project row knows which documents it actually has.
        const [contractRes, proposalRes] = await Promise.all([
          graphqlRequest(FIND_CUSTOMER_CONTRACTS).catch(() => null),
          graphqlRequest(FIND_CUSTOMER_PROPOSALS).catch(() => null),
        ]);
        setContracts(
          Array.isArray(contractRes?.findAllClientContracts)
            ? contractRes.findAllClientContracts
            : [],
        );
        setProposals(
          Array.isArray(proposalRes?.findAllProposals)
            ? proposalRes.findAllProposals
            : [],
        );

        // Booking requests this client raised from the client portal. Matching
        // on email + name mirrors how the backend groups customers, so a
        // shared address never surfaces another person's request here.
        const email = String(customer.email || '').trim().toLowerCase();
        const requestRes = email
          ? await graphqlRequest(FIND_CLIENT_REQUESTS, {
              page: 1,
              limit: 100,
            }).catch(() => null)
          : null;
        // Email is the customer's unique key, so every pending request on that
        // address belongs to this customer whatever contact name was typed.
        setClientRequests(
          (requestRes?.findAllBooking?.data || []).filter((row) => {
            const rowEmail = String(row.email || '').trim().toLowerCase();
            return rowEmail === email && isPendingLead(row);
          }),
        );
      } catch (err) {
        console.error(err);
        setError(err?.message || 'Failed to load customer records.');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [customer],
  );

  useEffect(() => {
    if (!customer?.id) return;
    setCreatingQuote(false);
    setCreatingProject(false);
    setCreatingProposal(false);
    setCreatingContract(false);
    setCreatingInvoice(false);
    setWritingEmail(false);
    setMessaging(false);
    setAcceptingRequest(null);
    setEnquiryRequest(null);
  }, [customer?.id]);

  useEffect(() => {
    if (!customer?.id) return;
    loadRecords();
  }, [customer?.id, loadRecords]);

  // Recent: accepted (Draft/pending after booking accept) and ongoing (active).
  const recentProjects = projects.filter(isRecentCustomerProject);

  const paidInvoices = invoices.filter(isPaidInvoice);
  const dueInvoices = invoices.filter((row) => !isPaidInvoice(row));
  const paidTotal = moneySum(paidInvoices);
  const dueTotal = moneySum(dueInvoices);
  const billedTotal = moneySum(invoices);
  const partialInvoices = invoices.filter((row) => {
    const key = String(row?.paymentStatus || '')
      .toLowerCase()
      .replace(/[\s_-]/g, '');
    return key === 'partiallypaid';
  });

  const ongoingCount = recentProjects.length;
  const completedCount = projects.filter((project) => {
    const key = String(project.status || '').toLowerCase();
    return key === 'complete' || key === 'completed';
  }).length;

  const monthBuckets = useMemo(() => {
    const buckets = lastSixMonthBuckets();
    const byKey = new Map(buckets.map((bucket) => [bucket.key, bucket]));
    invoices.forEach((invoice) => {
      const bucket = byKey.get(invoiceMonthKey(invoice));
      if (!bucket) return;
      const amount = Number(invoice.listTotal) || 0;
      if (isPaidInvoice(invoice)) bucket.income += amount;
      else bucket.outstanding += amount;
    });
    return buckets;
  }, [invoices]);

  const chartMax = Math.max(
    ...monthBuckets.flatMap((bucket) => [bucket.income, bucket.outstanding]),
    1,
  );
  const marginPercent =
    billedTotal > 0 ? Math.round((paidTotal / billedTotal) * 100) : 0;

  /**
   * Documents for one project.
   *
   * A quotation points at its project directly. A contract only names the
   * quotation it came from, so it reaches the project through that. Proposals
   * carry no project reference at all, so they resolve via a contract that
   * names them, falling back to the client's proposals.
   */
  /** Only documents really tied to this project (no client-wide fallback). */
  const linkedDocumentsFor = (project, type) => {
    const code = String(project?.projectId || '');
    const projectQuotes = quotations.filter(
      (row) => String(row.listProjectCode || '') === code,
    );
    if (type === 'Quotation') return projectQuotes;

    const numbers = new Set(
      projectQuotes.map((row) => String(row.quotationNumber || '')).filter(Boolean),
    );
    const projectContracts = contracts.filter((row) =>
      numbers.has(String(row.quotationNumber || '')),
    );
    if (type === 'Contract') return projectContracts;

    const referenced = new Set(
      projectContracts.map((row) => String(row.proposalNumber || '')).filter(Boolean),
    );
    // Proposals raised from a project are tagged with its series root.
    const projectIds = new Set([project?.id, project?.seriesRootId].filter(Boolean));
    return proposals.filter(
      (row) =>
        referenced.has(String(row.proposalNumber || '')) ||
        projectIds.has(row.projectId),
    );
  };

  const documentsForProject = (project, type) => {
    const linked = linkedDocumentsFor(project, type);
    if (type !== 'Proposal' || linked.length > 0) return linked;
    const email = String(customer.email || '').trim().toLowerCase();
    return email
      ? proposals.filter(
          (row) => String(row.clientEmail || '').toLowerCase() === email,
        )
      : [];
  };

  const openProjectDocuments = (project, type) => {
    const records = documentsForProject(project, type);
    if (records.length === 0) {
      toast.info(`No ${type.toLowerCase()} linked to ${project.projectId || 'this project'} yet.`);
      return;
    }
    setDocModal({ type, project, records });
  };

  const openProject = (id, tab) => {
    setViewingProject({ id, tab: tab || 'Overview' });
  };

  /**
   * Accepting a client-portal request goes straight to the form that matches
   * what the client asked for: a quotation request opens the quotation form,
   * a "Book a service" request opens the project form with that service.
   */
  const acceptRequest = (request) => {
    if (!request?.id) return;
    if (isServiceBooking(request)) setAcceptingRequest(request);
    else setEnquiryRequest(request);
  };

  const handleEnquiryQuoted = async () => {
    const request = enquiryRequest;
    setEnquiryRequest(null);
    if (!request?.id) {
      await loadRecords(true);
      return;
    }
    try {
      await graphqlRequest(UPDATE_BOOKING_STATUS, {
        id: request.id,
        status: 'APPROVED',
      });
      toast.success('Quotation request accepted and quotation saved.');
      window.dispatchEvent(new Event('lead:changed'));
    } catch (err) {
      console.error('Quotation saved but the request status did not update:', err);
      toast.warn(
        'Quotation saved, but the request is still marked pending. Update it from the lead pipeline.',
      );
    }
    await loadRecords(true);
  };

  const handleRequestAccepted = async () => {
    const request = acceptingRequest;
    setAcceptingRequest(null);
    if (!request?.id) {
      await loadRecords(true);
      return;
    }
    try {
      await graphqlRequest(UPDATE_BOOKING_STATUS, {
        id: request.id,
        status: 'APPROVED',
      });
      toast.success('Request accepted and project created.');
      // Keep the Lead pipeline in step with the decision made here.
      window.dispatchEvent(new Event('lead:changed'));
    } catch (err) {
      console.error('Project created but the request status did not update:', err);
      toast.warn(
        'Project created, but the request is still marked pending. Update it from the lead pipeline.',
      );
    }
    await loadRecords(true);
  };

  const declineRequest = async (request) => {
    if (!request?.id || decidingRequestId) return;
    const ok = await appConfirmMessage({
      title: 'Decline booking request',
      message: `Decline the request from ${request.fullName || 'this client'}? It will be marked as lost.`,
      confirmLabel: 'Decline',
      danger: true,
    });
    if (!ok) return;

    setDecidingRequestId(request.id);
    try {
      await graphqlRequest(UPDATE_BOOKING_STATUS, {
        id: request.id,
        status: 'CANCELLED',
      });
      toast.success('Booking request declined.');
      window.dispatchEvent(new Event('lead:changed'));
      await loadRecords(true);
    } catch (err) {
      console.error('Failed to decline request:', err);
      toast.error(err?.message || 'Could not decline the request');
    } finally {
      setDecidingRequestId(null);
    }
  };

  const startCreateQuote = () => {
    if (!customer?.id) {
      toast.error('This customer has no linked client record.');
      return;
    }
    setCreatingQuote(true);
  };

  const startCreateProject = () => {
    if (!customer?.id) {
      toast.error('This customer has no linked client record.');
      return;
    }
    setCreatingProject(true);
  };

  const startCreateInvoice = () => {
    if (!customer?.id) {
      toast.error('This customer has no linked client record.');
      return;
    }
    setCreatingInvoice(true);
  };

  const finishCreateQuote = () => {
    setCreatingQuote(false);
    loadRecords(true);
  };

  const openProposalCreate = () => {
    if (!customer?.id) {
      toast.error('This customer has no linked client record.');
      return;
    }
    setCreatingProposal(true);
  };

  const openContractCreate = () => {
    if (!customer?.id) {
      toast.error('This customer has no linked client record.');
      return;
    }
    setCreatingContract(true);
  };

  const startCall = () => setCalling(true);

  /** Chat with the client — appears in their Client Portal messages. */
  const startMessage = () => {
    if (!customer?.id) {
      toast.error('This customer has no linked client record.');
      return;
    }
    setMessaging(true);
  };

  const projectInvoices = (project) =>
    invoices.filter((invoice) => {
      const code = String(invoice.listProjectCode || '');
      const name = String(invoice.listProjectName || '').toLowerCase();
      return (
        (project.projectId && code === String(project.projectId)) ||
        (project.name && name === String(project.name || '').toLowerCase())
      );
    });

  if (!customer) return null;

  const address = [customer.address, customer.postcode].filter(Boolean).join(', ');
  const isRepeat = Number(customer.approvedLeadCount) > 1;
  const portalOn = Boolean(customer.portalCredentialsSentAt);

  // Project detail replaces the profile body but stays on the Customer page,
  // so the sidebar selection does not jump to Projects.
  if (viewingProject) {
    return (
      <div className="cm-profile">
        <p className="cm-crumb">
          SAS Admin <span>/</span> Customer Management <span>/</span>{' '}
          {customer.fullName || 'Customer'}
        </p>
        <Suspense fallback={<div className="pdx-empty">Loading project…</div>}>
          <CustomerProjectDetail
            projectId={viewingProject.id}
            initialTab={viewingProject.tab}
            onBack={() => {
              setViewingProject(null);
              loadRecords(true);
            }}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="cm-profile">
      <button type="button" className="cm-back" onClick={onBack}>
        <FaArrowLeft /> Customer Management
      </button>
      <p className="cm-crumb">
        SAS Admin <span>/</span> Customer Management <span>/</span>{' '}
        {customer.fullName || 'Customer'}
      </p>

      <section className="cm-hero">
        <div className="cm-hero-top">
          <div className="cm-hero-main">
            <div className="cm-hero-avatar">
              {customerInitials(customer.fullName)}
            </div>
            <div>
              <h1>{customer.fullName || 'Unnamed customer'}</h1>
              <p>Customer since {formatDate(customer.createdAt)}</p>
            </div>
          </div>
          <div className="cm-hero-actions">
            <button type="button" className="cm-hero-btn" onClick={onEdit}>
              <FiEdit2 /> Edit
            </button>
            <button
              type="button"
              className="cm-hero-btn cm-hero-btn--primary"
              onClick={() => setWritingEmail(true)}
              disabled={!customer.email}
            >
              <FiMail /> Send Email
            </button>
            <button type="button" className="cm-hero-btn" onClick={startMessage}>
              <FiMessageCircle /> Send Message
            </button>
            <button type="button" className="cm-hero-btn" onClick={startCall}>
              <FiPhone /> Call
            </button>
          </div>
        </div>
        <div className="cm-hero-meta">
          <span>
            <FaPhoneAlt /> {customer.phone || 'No phone'}
          </span>
          <span>
            <FaEnvelope /> {customer.email || 'No email'}
          </span>
          <span>{address || 'No address'}</span>
          <span className={`cm-portal-pill ${portalOn ? 'is-on' : ''}`}>
            Portal {portalOn ? ': Enabled' : ': Not invited'}
          </span>
          {isRepeat ? (
            <span className="cm-repeat-pill">
              <FaStar /> Repeat Customer
            </span>
          ) : null}
        </div>
      </section>

      <div className="cm-stat-row">
        <article>
          <strong>{loading ? '…' : projects.length}</strong>
          <span>Total Projects</span>
        </article>
        <article className="cm-stat-row__ongoing">
          <strong>{loading ? '…' : ongoingCount}</strong>
          <span>Ongoing</span>
        </article>
        <article className="cm-stat-row__done">
          <strong>{loading ? '…' : completedCount}</strong>
          <span>Completed</span>
        </article>
        <article>
          <strong>{loading ? '…' : formatAUD(paidTotal)}</strong>
          <span>Total income</span>
        </article>
        <article className="cm-stat-row__due">
          <strong>{loading ? '…' : formatAUD(dueTotal)}</strong>
          <span>Outstanding</span>
        </article>
        <article className="cm-stat-row__net">
          <strong>{loading ? '…' : formatAUD(paidTotal)}</strong>
          <span>Collected</span>
        </article>
      </div>

      <div className="cm-quick-row">
        <div className="cm-quick-left">
          <span>Quick Create:</span>
          <button type="button" className="customer-create-btn primary" onClick={startCreateQuote}>
            <FaPlus /> New Quotation
          </button>
          <button type="button" className="customer-create-btn" onClick={openProposalCreate}>
            <FaPlus /> New Proposal
          </button>
          <button type="button" className="customer-create-btn" onClick={openContractCreate}>
            <FaPlus /> New Contract
          </button>
          <button type="button" className="customer-create-btn" onClick={startCreateInvoice}>
            <FaPlus /> Custom invoice
          </button>
        </div>
        <button type="button" className="cm-new-project" onClick={startCreateProject}>
          <FaPlus /> New Project
        </button>
      </div>

      {error ? <p className="customer-muted">{error}</p> : null}

      <div className="cm-chart-grid">
        <section className="cm-card">
          <h3>Revenue vs outstanding (6 months)</h3>
          <div className="cm-bars" aria-hidden="true">
            {monthBuckets.map((bucket) => (
              <div className="cm-bar-col" key={bucket.key}>
                <div className="cm-bar-pair">
                  <span
                    className="cm-bar cm-bar--income"
                    style={{
                      height: `${Math.max(8, (bucket.income / chartMax) * 120)}px`,
                    }}
                  />
                  <span
                    className="cm-bar cm-bar--due"
                    style={{
                      height: `${Math.max(6, (bucket.outstanding / chartMax) * 120)}px`,
                    }}
                  />
                </div>
                <small>{bucket.label}</small>
              </div>
            ))}
          </div>
          <div className="cm-legend">
            <span>
              <i className="cm-dot cm-dot--income" /> Income
            </span>
            <span>
              <i className="cm-dot cm-dot--due" /> Outstanding
            </span>
          </div>
        </section>
        <section className="cm-card cm-card--center">
          <h3>Collection rate</h3>
          <div
            className="cm-donut"
            style={{
              background: `conic-gradient(var(--brand-primary, #6d28d9) ${marginPercent}%, #e2e8f0 0)`,
            }}
          >
            <strong>{loading ? '…' : `${marginPercent}%`}</strong>
          </div>
          <p>{formatAUD(paidTotal)} collected of {formatAUD(billedTotal)}</p>
        </section>
        <section className="cm-card">
          <h3>Payment status</h3>
          <ul className="cm-pay-list">
            <li>
              <span>Fully Paid</span>
              <b>{paidInvoices.length}</b>
              <div className="cm-pay-track">
                <em
                  className="is-paid"
                  style={{
                    width: `${invoices.length ? (paidInvoices.length / invoices.length) * 100 : 0}%`,
                  }}
                />
              </div>
            </li>
            <li>
              <span>Partially Paid</span>
              <b>{partialInvoices.length}</b>
              <div className="cm-pay-track">
                <em
                  className="is-partial"
                  style={{
                    width: `${invoices.length ? (partialInvoices.length / invoices.length) * 100 : 0}%`,
                  }}
                />
              </div>
            </li>
            <li>
              <span>Unpaid</span>
              <b>{dueInvoices.length}</b>
              <div className="cm-pay-track">
                <em
                  className="is-unpaid"
                  style={{
                    width: `${invoices.length ? (dueInvoices.length / invoices.length) * 100 : 0}%`,
                  }}
                />
              </div>
            </li>
          </ul>
        </section>
      </div>

      {clientRequests.length ? (
        <section className="cm-request-card">
          <div className="cm-request-head">
            <h3>
              <FaBell aria-hidden /> Pending Booking Requests
              <em>{clientRequests.length}</em>
            </h3>
            <p>Accept as an enquiry (quotation), or add a project</p>
          </div>
          <div className="customer-table-wrap">
            <table className="customer-mini-table cm-request-table">
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Service requested</th>
                  <th>Address</th>
                  <th>Requested</th>
                  <th>Preferred date</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clientRequests.map((request) => {
                  const busy = decidingRequestId === request.id;
                  const address = request.address || '—';
                  const preferred = request.preferredDate;
                  return (
                    <tr key={request.id}>
                      <td>
                        <button
                          type="button"
                          className="cm-request-ref"
                          onClick={() => acceptRequest(request)}
                          title={
                            isServiceBooking(request)
                              ? 'Accept and create the project'
                              : 'Accept and prepare a quotation'
                          }
                        >
                          {requestRef(request.id)}
                        </button>
                      </td>
                      <td>
                        <strong>{serviceRequested(request)}</strong>
                        <small className="cm-request-kind">
                          {isServiceBooking(request) ? 'Book a service' : 'Quotation request'}
                        </small>
                      </td>
                      <td className="cm-request-soft">{address}</td>
                      <td className="cm-request-soft">
                        {formatDate(request.createdAt)}
                      </td>
                      <td>
                        <strong>
                          {preferred ? formatDate(preferred) : '—'}
                        </strong>
                      </td>
                      <td className="cm-request-soft">
                        {request.message || '—'}
                      </td>
                      <td>
                        <div className="cm-request-actions">
                          <button
                            type="button"
                            className="cm-accept"
                            disabled={busy}
                            onClick={() => acceptRequest(request)}
                          >
                            {busy ? '…' : 'Accept'}
                          </button>
                          <button
                            type="button"
                            className="cm-decline"
                            disabled={busy}
                            onClick={() => declineRequest(request)}
                          >
                            Decline
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="cm-card">
        <div className="cm-section-head">
          <div>
            <h3>Recent projects</h3>
            <p>Accepted and ongoing projects for this customer.</p>
          </div>
        </div>
        {loading ? (
          <EmptyHint>Loading projects…</EmptyHint>
        ) : recentProjects.length ? (
          <div className="customer-table-wrap">
            <table className="customer-mini-table cm-wide-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Documents</th>
                  <th>Open documents</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentProjects.map((project) => {
                  const available = ['Quotation', 'Proposal', 'Contract'].filter(
                    (kind) => linkedDocumentsFor(project, kind).length > 0,
                  );
                  return (
                    <tr key={project.id}>
                      <td>
                        <strong>{project.projectId || 'Project'}</strong>
                        <small>{project.name || 'Untitled'}</small>
                      </td>
                      <td>{formatDate(project.startDate || project.deadLine)}</td>
                      <td>
                        <span
                          className={`customer-status-chip ${projectTone(project.status)}`}
                        >
                          {String(project.status || '').toLowerCase() === 'active'
                            ? 'Ongoing'
                            : String(project.status || '').toLowerCase() === 'pending'
                              ? 'Accepted'
                              : mapStatusToDisplay(project.status)}
                        </span>
                      </td>
                      <td>
                        {available.length ? (
                          <span className="cm-doc-list">
                            {available.map((kind) => (
                              <span key={kind} className="cm-doc is-on" title={kind}>
                                {kind[0]}
                              </span>
                            ))}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {/*
                          An existing project's documents are viewed, not
                          created from here — each opens that project's own tab,
                          which lists what exists and can raise a new one.
                        */}
                        <div className="cm-quick-mini">
                          {['Quotation', 'Proposal', 'Contract'].map((kind) => (
                            <button
                              key={kind}
                              type="button"
                              onClick={() => openProjectDocuments(project, kind)}
                            >
                              {kind}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="customer-inline-link"
                          onClick={() => openProject(project.id)}
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyHint>No accepted or ongoing projects yet.</EmptyHint>
        )}
      </section>

      <section className="cm-card">
        <div className="cm-section-head">
          <div>
            <h3>Project history</h3>
            <p>{projects.length} project{projects.length === 1 ? '' : 's'}</p>
          </div>
        </div>
        {loading ? (
          <EmptyHint>Loading history…</EmptyHint>
        ) : projects.length ? (
          <div className="customer-table-wrap">
            <table className="customer-mini-table cm-wide-table">
              <thead>
                <tr>
                  <th>Project ID</th>
                  <th>Name</th>
                  <th>Dates</th>
                  <th>Billed</th>
                  <th>Outstanding</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => {
                  const rows = projectInvoices(project);
                  const billed = moneySum(rows);
                  const outstanding = moneySum(rows.filter((row) => !isPaidInvoice(row)));
                  const paid = rows.length > 0 && rows.every(isPaidInvoice);
                  const partial = rows.some(isPaidInvoice) && outstanding > 0;
                  return (
                    <tr key={`hist-${project.id}`}>
                      <td>{project.projectId || '—'}</td>
                      <td>
                        <strong>{project.name || 'Untitled'}</strong>
                        <small>{formatDateRange(project.startDate, project.deadLine)}</small>
                      </td>
                      <td>{formatDate(project.startDate)}</td>
                      <td>{formatAUD(billed)}</td>
                      <td>{formatAUD(outstanding)}</td>
                      <td>
                        <span
                          className={`customer-pay-chip ${paid ? 'paid' : 'due'}`}
                        >
                          {paid
                            ? 'Fully Paid'
                            : partial
                              ? 'Partially Paid'
                              : rows.length
                                ? 'Unpaid'
                                : '—'}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`customer-status-chip ${projectTone(project.status)}`}
                        >
                          {mapStatusToDisplay(project.status)}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="customer-inline-link"
                          onClick={() => openProject(project.id)}
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyHint>No project history yet.</EmptyHint>
        )}
      </section>

      {creatingQuote || enquiryRequest ? (
        <Suspense fallback={null}>
          {createPortal(
            <ProfileBookingProvider
              bookingId={enquiryRequest?.id || customer.id}
              lead={enquiryRequest || customer}
            >
              <QuotationForm
                closeModal={() => {
                  setCreatingQuote(false);
                  setEnquiryRequest(null);
                }}
                onSaved={enquiryRequest ? handleEnquiryQuoted : finishCreateQuote}
                onIssue={(saved) => {
                  const issueBookingId = enquiryRequest?.id || customer.id;
                  if (enquiryRequest) handleEnquiryQuoted();
                  else finishCreateQuote();
                  if (saved?.id) setIssuingQuote({ ...saved, bookingId: issueBookingId });
                }}
              />
            </ProfileBookingProvider>,
            document.body,
          )}
        </Suspense>
      ) : null}
      {issuingQuote ? (
        <Suspense fallback={null}>
          <QuotationSendEmailModal
            bookingId={issuingQuote.bookingId}
            quote={issuingQuote}
            clientName={customer.fullName}
            onClose={() => setIssuingQuote(null)}
            onSent={() => loadRecords(true)}
          />
        </Suspense>
      ) : null}
      {creatingProject ? (
        <Suspense fallback={null}>
          <AddProject
            initialBookingId={customer.id}
            initialClient={customer}
            onCreated={() => {
              loadRecords(true);
            }}
            onClose={() => setCreatingProject(false)}
          />
        </Suspense>
      ) : null}
      {acceptingRequest ? (
        <Suspense fallback={null}>
          {/*
            Accepting a portal request opens the normal project form, prefilled
            from the request, so the admin reviews and completes it before the
            project exists. The booking is approved only after it is created.
          */}
          <AddProject
            initialBookingId={acceptingRequest.id}
            initialClient={acceptingRequest}
            preselectRequestedServices
            onCreated={handleRequestAccepted}
            onClose={() => setAcceptingRequest(null)}
          />
        </Suspense>
      ) : null}
      {docModal ? (
        <Suspense fallback={null}>
          <ProfileBookingProvider bookingId={customer.id} lead={customer}>
            <ProjectDocumentModal
              type={docModal.type}
              project={docModal.project}
              records={docModal.records}
              onClose={() => setDocModal(null)}
            />
          </ProfileBookingProvider>
        </Suspense>
      ) : null}
      {creatingInvoice ? (
        <Suspense fallback={null}>
          <CustomerCreateInvoice
            customer={customer}
            projects={projects}
            onClose={() => setCreatingInvoice(false)}
            onCreated={() => loadRecords(true)}
            onNeedProject={startCreateProject}
          />
        </Suspense>
      ) : null}
      {creatingProposal ? (
        <Suspense fallback={null}>
          <CreateProposalModal
            prefill={proposalPrefillFromClient(customer)}
            onClose={() => setCreatingProposal(false)}
            onCreated={() => {
              setCreatingProposal(false);
              loadRecords(true);
            }}
          />
        </Suspense>
      ) : null}
      {creatingContract ? (
        <Suspense fallback={null}>
          <CustomerCreateContract
            // Already inside this customer: the form fills the client details from the lead.
            prefill={{ bookingId: customer.id }}
            onClose={() => setCreatingContract(false)}
            onSaved={() => {
              setCreatingContract(false);
              loadRecords(true);
            }}
          />
        </Suspense>
      ) : null}
      {writingEmail ? (
        <Suspense fallback={null}>
          <CustomerEmailModal
            customer={customer}
            projects={projects}
            onClose={() => setWritingEmail(false)}
          />
        </Suspense>
      ) : null}
      {calling ? (
        <Suspense fallback={null}>
          <CustomerCallModal
            customer={customer}
            projects={projects}
            onClose={() => setCalling(false)}
          />
        </Suspense>
      ) : null}
      {messaging ? (
        <Suspense fallback={null}>
          <ProfileBookingProvider bookingId={customer.id} lead={customer}>
            <CustomerMessageModal
              customer={customer}
              onClose={() => setMessaging(false)}
            />
          </ProfileBookingProvider>
        </Suspense>
      ) : null}
    </div>
  );
}
